

















































import { parseInt } from "lodash-es";

import {
  defineComponent,
  reactive,
  toRef,
  toRefs,
  PropType,
  ref,
  onMounted,
  computed,
} from "@vue/composition-api";

import { DivisionType, CustomerType, GetAddressesReturnType } from "@/api/sap";

import { pushEvent } from "@/helper/pushEvent.helper";

import SapPostalCodeField from "@/features/shared/SapPostalCodeField";

import {
  useConsumptionSliderOptions,
  useTariffCalcSubmit,
  useInitTariffCalc,
} from "@/features/tariff-calculator/hooks";

import { tariffCalculatorConstants } from "@/features/tariff-calculator/constants";
import { TariffCalculatorConfig } from "@/features/tariff-calculator/components/TariffCalculator";
import TariffCalculatorConsumptionSlider from "@/features/tariff-calculator/components/TariffCalculatorConsumptionSlider";
import TariffCalculatorCustomerType from "@/features/tariff-calculator/components/TariffCalculatorCustomerType";
import TariffCalculatorForm from "@/features/tariff-calculator/components/TariffCalculatorForm";

interface TariffCalculatorElectricityViewState {
  affiliateId?: string;
  calculatorMode: keyof typeof DivisionType;
  customerType: CustomerType;
  address: GetAddressesReturnType | undefined;
  powerConsumption: string | undefined;
  allowedProductIds?: string;
  serviceNo?: string;
}

export default defineComponent({
  name: "TariffCalculatorElectricityView",
  components: {
    TariffCalculatorConsumptionSlider,
    TariffCalculatorCustomerType,
    TariffCalculatorForm,
    SapPostalCodeField,
  },
  props: {
    config: {
      type: Object as PropType<TariffCalculatorConfig>,
      default: () => ({} as TariffCalculatorConfig),
    },
    initialValues: {
      type: Object as PropType<Partial<TariffCalculatorElectricityViewState>>,
      default: () => ({} as Partial<TariffCalculatorElectricityViewState>),
    },
    shouldRedirect: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const isSingleCustomerType = ref(false);
    const isCalculatorTypeMini = ref(false);

    onMounted(() => {
      if (props.config.customerType) {
        isSingleCustomerType.value = true;
      }

      if (props.config.calculatorMini) {
        isCalculatorTypeMini.value = props.config.calculatorMini;
      }
    });

    const defaultState: TariffCalculatorElectricityViewState = {
      calculatorMode: "Electricity" as keyof typeof DivisionType,
      customerType: (props.config.customerType as keyof typeof CustomerType)
        ? CustomerType[props.config.customerType as keyof typeof CustomerType]
        : CustomerType.PrivateCustomer,
      address: undefined,
      powerConsumption: undefined,
      allowedProductIds: props.config.allowedProductIds,
      serviceNo: props.config.serviceNo
        ? props.config.serviceNo.electricity
        : undefined,
    };

    const { getInitialStateValues } = useInitTariffCalc(
      defaultState,
      props.initialValues
    );

    const state = reactive<TariffCalculatorElectricityViewState>({
      ...defaultState,
      ...getInitialStateValues(),
    });

    const {
      sliderOptions,
      fillIconItems,
      iconName,
    } = useConsumptionSliderOptions({
      mode: toRef(state, "calculatorMode"),
      customerType: toRef(state, "customerType"),
    });

    const powerConsumptionLimit = computed(
      () => tariffCalculatorConstants.limits.Electricity[state.customerType]
    );

    const customResultUrl = computed(() =>
      parseInt(state.powerConsumption ?? "") > powerConsumptionLimit.value
        ? props.config.resultURLService
        : props.config.resultURL
    );

    const { submitCalculator } = useTariffCalcSubmit(
      customResultUrl,
      props.shouldRedirect
    );

    function onSubmit() {
      pushEvent({
        event: "calculate",
        eventCategory: "Tarifrechner",
        eventAction: "Tarif berechnen",
        eventLabel: "Strom",
      });

      submitCalculator({ values: state });
    }

    return {
      ...toRefs(state),
      fillIconItems,
      onSubmit,
      sliderOptions,
      iconName,
      isSingleCustomerType,
      isCalculatorTypeMini,
      powerConsumptionLimit,
    };
  },
});
